/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from 'react';

import { StatusCode } from '../../api/enumerations';
import {
  approvePreFlightRequest,
  completePreFlightRequest,
  getAllPreFlightRequests,
  getPreFlightCompanies,
  getPreFlightRequest,
  rejectPreFlightRequest,
  updatePreFlightWorkOrder,
} from '../../api/preFlight';
import {
  PreFlightDocumentType,
  PreFlightSource,
} from '../../api/preFlight/enumerations';
import {
  ApprovePreFlightParams,
  PreFlightCompanyData,
  PreFlightData,
  PreFlightDocumentData,
  PreFlightListData,
  RejectPreFlightParams,
  UpdateWorkOrderParams,
} from '../../api/preFlight/types';
import useErrorMessage from '../../hooks/useErrorMessage';
import useSnackbar from '../../hooks/useSnackbar';
import { Constants } from './constants';

export interface UsePreFlightProps {
  /** Origem selecionada no filtro. `''` = todas. */
  source: PreFlightSource | '';
  selectSource: (source: PreFlightSource | '') => void;
  companies: PreFlightCompanyData[];
  loadingCompanies: boolean;
  getCompaniesCallback: () => Promise<void>;
  preFlightRequests: PreFlightListData[];
  loadingRequests: boolean;
  selectedCompanyId: number | undefined;
  setSelectedCompanyId: (value: number | undefined) => void;
  page: number;
  setPage: (value: number) => void;
  totalPages: number;
  loadNextPage: () => void;
  selectedPreflight: PreFlightData | undefined;
  setSelectedPreflight: (value: PreFlightData | undefined) => void;
  loadingDetail: boolean;
  handleGetPreflight: (id: number) => Promise<void>;
  loadingApprove: boolean;
  loadingComplete: boolean;
  handleComplete: (id: number, file: File) => Promise<void>;
  loadingReject: boolean;
  loadingUpdateWorkOrder: boolean;
  handleApprove: (id: number, params?: ApprovePreFlightParams) => Promise<void>;
  handleReject: (id: number, params: RejectPreFlightParams) => Promise<void>;
  handleUpdateWorkOrder: (
    id: number,
    params: UpdateWorkOrderParams
  ) => Promise<boolean>;
  handleDeedUpload: (doc: PreFlightDocumentData) => void;
  handleDocumentAdded: (doc: PreFlightDocumentData) => void;
  handleDocumentDeleted: (reference: string) => void;
}

export function usePreFlight(): UsePreFlightProps {
  const [companies, setCompanies] = useState<PreFlightCompanyData[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const [preFlightRequests, setPreFlightRequests] = useState<
    PreFlightListData[]
  >([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<
    number | undefined
  >();
  /**
   * A origem selecionada. `''` = todas.
   *
   * Estado da página e não do card: o filtro atravessa a lista inteira e a paginação, e
   * guardá-lo dentro da lista faria a página voltar para a origem anterior a cada troca de
   * empresa.
   */
  const [source, setSource] = useState<PreFlightSource | ''>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const [selectedPreflight, setSelectedPreflight] = useState<
    PreFlightData | undefined
  >();
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [loadingApprove, setLoadingApprove] = useState(false);
  const [loadingComplete, setLoadingComplete] = useState(false);
  const [loadingReject, setLoadingReject] = useState(false);
  const [loadingUpdateWorkOrder, setLoadingUpdateWorkOrder] = useState(false);

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();

  const getCompaniesCallback = useCallback(async () => {
    setLoadingCompanies(true);
    try {
      const response = await getPreFlightCompanies();

      if (response.detail.status_code !== StatusCode.OK || !response.data) {
        throw new Error(response.detail.description ?? Constants.errorMessage);
      }

      setCompanies(response.data);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoadingCompanies(false);
    }
  }, []);

  useEffect(() => {
    getCompaniesCallback();
  }, [getCompaniesCallback]);

  useEffect(() => {
    setPage(1);
    setPreFlightRequests([]);
    setSelectedPreflight(undefined);
  }, [selectedCompanyId]);

  const getPreFlightRequestsCallback = useCallback(async () => {
    if (!selectedCompanyId) return;

    setLoadingRequests(true);
    try {
      const response = await getAllPreFlightRequests(
        page,
        6,
        selectedCompanyId,
        undefined,
        undefined,
        source
      );

      if (response.detail.status_code !== StatusCode.OK || !response.data) {
        throw new Error(response.detail.description ?? Constants.errorMessage);
      }

      const newItems = response.data ?? [];
      setPreFlightRequests((prev) =>
        page === 1 ? newItems : [...prev, ...newItems]
      );
      setTotalPages(response.detail.total_pages ?? 0);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoadingRequests(false);
    }
  }, [selectedCompanyId, page, source]);

  useEffect(() => {
    getPreFlightRequestsCallback();
  }, [getPreFlightRequestsCallback]);

  // Trocar de origem volta para a primeira página. Sem isso, filtrar estando na página 3
  // pode cair numa lista vazia — que o revisor leria como "não há nada nesta origem".
  const selectSource = useCallback((novaOrigem: PreFlightSource | '') => {
    setSource(novaOrigem);
    setPage(1);
  }, []);

  const handleGetPreflight = async (id: number): Promise<void> => {
    setLoadingDetail(true);
    try {
      const response = await getPreFlightRequest(id);

      if (response.detail.status_code !== StatusCode.OK || !response.data) {
        throw new Error(response.detail.description ?? Constants.errorMessage);
      }

      setSelectedPreflight(response.data);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleApprove = async (
    id: number,
    params?: ApprovePreFlightParams
  ): Promise<void> => {
    setLoadingApprove(true);
    try {
      const response = await approvePreFlightRequest(id, params);

      if (response.detail.status_code !== StatusCode.OK || !response.data) {
        throw new Error(response.detail.description ?? Constants.errorMessage);
      }

      const approved = response.data;
      setSelectedPreflight(approved);
      setPreFlightRequests((prev) =>
        prev.map((item) =>
          item.id !== approved.id ? item : { ...item, status: approved.status }
        )
      );
      setCompanies((prev) =>
        prev.map((c) =>
          c.id === selectedCompanyId ? { ...c, pending: c.pending - 1 } : c
        )
      );
      handleSnackbar(Constants.approvedSuccess, false);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoadingApprove(false);
    }
  };

  /**
   * Conclui a OS anexando o laudo.
   *
   * A Control arquiva a ordem como concluída e emite `report.delivered` para a fonte — no
   * caso do RPAM, é o que leva a avaliação de lá a "Entregue", com o valor de mercado e o
   * arquivo. Por isso a mensagem de sucesso diz que a origem foi avisada: é a única pista
   * na tela de que algo saiu daqui para outro sistema.
   */
  const handleComplete = async (id: number, file: File): Promise<void> => {
    setLoadingComplete(true);
    try {
      const response = await completePreFlightRequest(id, file);

      if (response.detail.status_code !== StatusCode.OK || !response.data) {
        throw new Error(response.detail.description ?? Constants.errorMessage);
      }

      setSelectedPreflight(response.data);
      handleSnackbar(Constants.completeOsSuccess, false);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoadingComplete(false);
    }
  };

  const handleReject = async (
    id: number,
    params: RejectPreFlightParams
  ): Promise<void> => {
    setLoadingReject(true);
    try {
      const response = await rejectPreFlightRequest(id, params);

      if (response.detail.status_code !== StatusCode.OK || !response.data) {
        throw new Error(response.detail.description ?? Constants.errorMessage);
      }

      const rejected = response.data;
      setSelectedPreflight(rejected);
      setPreFlightRequests((prev) =>
        prev.map((item) =>
          item.id !== rejected.id
            ? item
            : {
                ...item,
                status: rejected.status,
                rejection_reason: rejected.rejection_reason,
              }
        )
      );
      setCompanies((prev) =>
        prev.map((c) =>
          c.id === selectedCompanyId ? { ...c, pending: c.pending - 1 } : c
        )
      );
      handleSnackbar(Constants.rejectedSuccess, false);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoadingReject(false);
    }
  };

  const handleUpdateWorkOrder = async (
    id: number,
    params: UpdateWorkOrderParams
  ): Promise<boolean> => {
    setLoadingUpdateWorkOrder(true);
    try {
      const response = await updatePreFlightWorkOrder(id, params);

      if (response.detail.status_code !== StatusCode.OK || !response.data) {
        throw new Error(response.detail.description ?? Constants.errorMessage);
      }

      const updated = response.data;
      setSelectedPreflight(updated);
      setPreFlightRequests((prev) =>
        prev.map((item) => {
          if (item.id !== updated.id) return item;

          const addr = updated.address;
          const addressParts = [
            addr.street,
            addr.number,
            addr.neighborhood,
            [addr.city, addr.uf].filter(Boolean).join('/'),
          ].filter(Boolean);

          return {
            ...item,
            requester_name: updated.requester?.name ?? item.requester_name,
            work_order: {
              ...item.work_order,
              address_summary: addressParts.length
                ? addressParts.join(', ')
                : item.work_order.address_summary,
            },
          };
        })
      );
      handleSnackbar(Constants.updatedSuccess, false);
      return true;
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
      return false;
    } finally {
      setLoadingUpdateWorkOrder(false);
    }
  };

  const handleDeedUpload = (doc: PreFlightDocumentData): void => {
    if (!selectedPreflight) return;
    setSelectedPreflight({
      ...selectedPreflight,
      documents: [
        ...selectedPreflight.documents.filter(
          (d) => d.type !== PreFlightDocumentType.DEED
        ),
        doc,
      ],
    });
  };

  const handleDocumentAdded = (doc: PreFlightDocumentData): void => {
    if (!selectedPreflight) return;
    setSelectedPreflight({
      ...selectedPreflight,
      documents: [...selectedPreflight.documents, doc],
    });
  };

  const handleDocumentDeleted = (reference: string): void => {
    if (!selectedPreflight) return;
    setSelectedPreflight({
      ...selectedPreflight,
      documents: selectedPreflight.documents.filter(
        (d) => d.reference !== reference
      ),
    });
  };

  const loadNextPage = (): void => setPage((prev) => prev + 1);

  return {
    companies,
    source,
    selectSource,
    loadingCompanies,
    getCompaniesCallback,
    preFlightRequests,
    loadingRequests,
    selectedCompanyId,
    setSelectedCompanyId,
    page,
    setPage,
    totalPages,
    loadNextPage,
    selectedPreflight,
    setSelectedPreflight,
    loadingDetail,
    handleGetPreflight,
    loadingApprove,
    loadingComplete,
    handleComplete,
    loadingReject,
    loadingUpdateWorkOrder,
    handleApprove,
    handleReject,
    handleUpdateWorkOrder,
    handleDeedUpload,
    handleDocumentAdded,
    handleDocumentDeleted,
  };
}
